<script lang="ts">
  import { apiClient } from '@/utils/api';

  interface SearchUser {
    id: string | number;
    name: string;
    nickname?: string;
    avatarUrl?: string;
    levelName?: string;
  }

  interface Props {
    open: boolean;
    onClose: () => void;
    onStarted?: (userId: string | number) => void;
    isStarting?: boolean;
  }

  let { open = $bindable(), onClose, onStarted, isStarting = false }: Props = $props();

  let query = $state('');
  let results = $state<SearchUser[]>([]);
  let hasSearched = $state(false);
  let isSearching = $state(false);
  let errorMsg = $state('');

  // Debounced auto-search to keep server load low.
  // 350ms gives users time to finish typing; min 2 chars filters trivial requests;
  // lastQuery dedupes when the same string fires twice; reqId discards stale responses.
  const DEBOUNCE_MS = 350;
  const MIN_CHARS = 2;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let lastQuery = '';
  let reqId = 0;

  function scheduleSearch() {
    if (debounceTimer) clearTimeout(debounceTimer);
    const q = query.trim();
    errorMsg = '';

    if (q.length < MIN_CHARS) {
      results = [];
      hasSearched = false;
      lastQuery = '';
      isSearching = false;
      return;
    }

    debounceTimer = setTimeout(() => runSearch(q), DEBOUNCE_MS);
  }

  async function runSearch(q: string) {
    if (q === lastQuery) return;
    lastQuery = q;
    hasSearched = true;
    isSearching = true;
    const myReq = ++reqId;

    const response = await apiClient.get<{ users?: SearchUser[] }>('/api/search', {
      params: { q, type: 'users', limit: 8 },
    });

    // Drop response if a newer request started while this one was in flight.
    if (myReq !== reqId) return;

    if (response.success) {
      const payload = response.data as any;
      results = payload?.users ?? (response as any).users ?? [];
    } else {
      errorMsg = response.message || '사용자 검색에 실패했습니다.';
      results = [];
    }
    isSearching = false;
  }

  // Re-schedule whenever query changes.
  $effect(() => {
    void query;
    scheduleSearch();
  });

  function searchNow() {
    if (debounceTimer) clearTimeout(debounceTimer);
    runSearch(query.trim());
  }

  function pick(user: SearchUser) {
    onStarted?.(user.id);
  }

  function reset() {
    query = '';
    results = [];
    hasSearched = false;
    errorMsg = '';
  }

  function close() {
    reset();
    onClose();
  }
</script>

{#if open}
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
    <div class="w-full max-w-lg rounded-2xl bg-white dark:bg-dark-secondary border border-default shadow-xl p-5">
      <div class="flex items-start justify-between mb-3">
        <div>
          <h2 class="text-h2 text-content">새 메시지 보내기</h2>
          <p class="text-body2 text-content-tertiary mt-1">
            상대가 수락하기 전까지는 최대 3개까지 보낼 수 있어요.
          </p>
        </div>
        <button type="button" class="p-1 rounded hover:bg-gray-100 dark:hover:bg-dark-tertiary" aria-label="닫기" onclick={close}>
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <form class="space-y-3" onsubmit={(e) => { e.preventDefault(); search(); }}>
        <div class="flex gap-2">
          <input bind:value={query}
                 type="text"
                 class="input flex-1"
                 placeholder="이름 또는 닉네임 (2글자 이상)"
                 autocomplete="off" />
          <button type="submit" class="btn btn-primary btn-sm whitespace-nowrap" disabled={isSearching || isStarting}>
            {isSearching ? '검색 중...' : '검색'}
          </button>
        </div>
        {#if errorMsg}
          <p class="text-body2 text-error">{errorMsg}</p>
        {/if}
      </form>

      <div class="mt-4 max-h-[50vh] overflow-y-auto">
        {#if results.length > 0}
          <ul class="space-y-2">
            {#each results as user (user.id)}
              <li>
                <button type="button"
                        class="w-full flex items-center justify-between gap-3 rounded-xl border border-default bg-surface px-4 py-3 text-left hover:border-primary-300 hover:bg-primary-50/40 dark:hover:bg-primary/10 transition-colors disabled:opacity-50"
                        disabled={isStarting}
                        onclick={() => pick(user)}>
                  <div class="flex items-center gap-3 min-w-0">
                    <div class="w-10 h-10 rounded-full bg-primary-50 dark:bg-dark-tertiary text-primary dark:text-dark-text-high flex items-center justify-center font-semibold overflow-hidden">
                      {#if user.avatarUrl}
                        <img src={user.avatarUrl} alt={user.name} class="w-full h-full object-cover" />
                      {:else}
                        {(user.nickname || user.name).charAt(0).toUpperCase()}
                      {/if}
                    </div>
                    <div class="min-w-0">
                      <p class="font-semibold text-content truncate">{user.nickname || user.name}</p>
                      <p class="text-body2 text-content-tertiary truncate">
                        {user.nickname && user.nickname !== user.name ? user.name : user.levelName || '사용자'}
                      </p>
                    </div>
                  </div>
                  <span class="text-body2 font-medium text-primary shrink-0">
                    {isStarting ? '시작 중...' : '대화 시작'}
                  </span>
                </button>
              </li>
            {/each}
          </ul>
        {:else if hasSearched && !isSearching && !errorMsg}
          <div class="rounded-xl bg-surface-tertiary px-4 py-6 text-center text-content-tertiary">
            검색 결과가 없습니다.
          </div>
        {:else if !hasSearched}
          <div class="rounded-xl bg-surface-tertiary px-4 py-6 text-center text-content-tertiary text-body2">
            이름이나 닉네임을 입력해 검색해주세요.
          </div>
        {/if}
      </div>
    </div>
  </div>
{/if}
